import axios from 'axios';

import {
  isActiveSessionAuthGenerationCurrent,
  type ActiveSessionAuthGenerationSnapshot,
} from './activeSessionSync';
import { type SsoContext } from './ssoSession';
import { resolveApiRequestTimeout } from '@/shared/lib/apiRequestTimeout';

type StorageReader = Pick<Storage, 'getItem'>;
type TerminalRequest = (endpoint: string, authorization?: string) => Promise<number | void>;
export type TerminalSsoExpiryOutcome = 'settled' | 'unavailable' | 'superseded';

const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (typeof window === 'undefined' ? undefined : window.location.origin);
const TERMINAL_TIMEOUT_MS = resolveApiRequestTimeout(
  import.meta.env.VITE_SSO_TERMINAL_TIMEOUT_MS,
  'VITE_SSO_TERMINAL_TIMEOUT_MS',
);
const terminalClient = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : undefined,
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: TERMINAL_TIMEOUT_MS,
});
const inFlight = new Map<string, Promise<TerminalSsoExpiryOutcome>>();

export const terminalSsoExpiryEndpoint = (context: SsoContext): string => {
  switch (context.entryType) {
    case 'admin':
      return `/admin/${context.site}/sso/session-expired`;
    case 'clinic':
      return `/clinic/${context.site}/auth/sso/session-expired`;
    case 'unified':
      return `/portal/${context.site}/auth/sso/session-expired`;
  }
};

export const exactAuthorizationHeader = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const candidate = 'get' in headers && typeof headers.get === 'function'
    ? headers.get('Authorization')
    : (headers as Record<string, unknown>).Authorization
      ?? (headers as Record<string, unknown>).authorization;

  return typeof candidate === 'string'
    && /^Bearer [0-9]+\|[A-Za-z0-9]{40,256}$/.test(candidate)
      ? candidate
      : undefined;
};

export const retryTerminalRequest = async (
  request: () => Promise<number | void>,
): Promise<number | void> => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const status = await request();
    if (status !== 202 || attempt === 1) {
      return status;
    }
  }
};

const defaultRequest = async (
  endpoint: string,
  authorization?: string,
  _force = false,
): Promise<number | void> => {
  return retryTerminalRequest(async () => {
    const response = await terminalClient.post(endpoint, {}, {
      headers: authorization ? { Authorization: authorization } : undefined,
    });

    return response.status;
  });
};

export const notifyTerminalSsoExpiry = ({
  context,
  generation,
  authorization,
  localStorage,
  request,
  force = false,
}: {
  context: SsoContext;
  generation: ActiveSessionAuthGenerationSnapshot;
  authorization?: string;
  localStorage: StorageReader;
  request?: TerminalRequest;
  force?: boolean;
}): Promise<TerminalSsoExpiryOutcome> => {
  if (!isActiveSessionAuthGenerationCurrent(generation, localStorage)) {
    return Promise.resolve('superseded');
  }

  const key = `${context.site}:${context.entryType}:${generation.generation}`;
  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }

  const terminalRequest = request ?? ((endpoint, auth) => defaultRequest(endpoint, auth, force));
  const pending = terminalRequest(terminalSsoExpiryEndpoint(context), authorization)
    .then(status => status === 204 ? 'settled' as const : 'unavailable' as const)
    .catch(() => {
      // Local cleanup/navigation is authoritative even when the broker is unavailable.
      return 'unavailable' as const;
    })
    .finally(() => {
      if (inFlight.get(key) === pending) {
        inFlight.delete(key);
      }
    });
  inFlight.set(key, pending);

  return pending;
};
